import { NavLink } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    LayoutDashboard, CalendarDays, Layers3, ClipboardCheck, Wallet, BookOpen, Users, Settings,
    Building2, ShieldCheck, UserCog, ScrollText, ChevronDown, X,
    ShoppingCart, Boxes, BarChart3, Truck, FileText, Package, Wrench,
    KeyRound, ShieldAlert, LifeBuoy, Trash2, Calendar, Mail, Globe,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";

type Item = {
    to: string;
    labelKey: string;
    icon: React.ComponentType<{ size?: number }>;
    perm?: string;
    activity?: string;
    module?: string;
    children?: Item[];
};

// Kept in lock-step with `/components/shell/Sidebar.tsx`. Any change here MUST
// be mirrored there — same permission gates, same module gates, same TAC-1
// (`hasModuleAccess`) usage — otherwise mobile users see a different menu.
const NAV: Item[] = [
    { to: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, module: "dashboard" },
    { to: "/people", labelKey: "nav.people", icon: Users, perm: "people.view", module: "people" },
    { to: "/programs", labelKey: "nav.programs", icon: BookOpen, perm: "program.view", module: "programs" },
    { to: "/groups", labelKey: "nav.groups", icon: Layers3, perm: "group.view", module: "groups" },
    { to: "/schedule", labelKey: "nav.schedule", icon: CalendarDays, perm: "schedule.view", module: "schedule" },
    { to: "/tasks", labelKey: "nav.tasks", icon: ClipboardCheck, perm: "tasks.view", module: "tasks" },
    {
        to: "/hr", labelKey: "nav.hr", icon: UserCog, perm: "hr.view", module: "hr",
        children: [
            { to: "/hr", labelKey: "nav.hr_employees", icon: UserCog, perm: "hr.view", module: "hr" },
            { to: "/hr/payroll", labelKey: "nav.hr_payroll", icon: Wallet, perm: "payroll.view", module: "hr.payroll" },
            { to: "/hr/payroll/runs", labelKey: "nav.hr_payroll_runs", icon: ScrollText, perm: "payroll.view", module: "hr.payroll" },
        ],
    },
    { to: "/crm", labelKey: "nav.crm", icon: Users, perm: "crm.view", module: "crm" },
    { to: "/support", labelKey: "nav.support", icon: LifeBuoy, perm: "support.use", module: "support" },
    { to: "/trash", labelKey: "nav.trash", icon: Trash2, perm: "trash.manage", module: "trash" },
    {
        to: "/kindergarten", labelKey: "nav.kindergarten", icon: BookOpen, perm: "kg.view", activity: "kindergarten", module: "kindergarten",
        children: [
            { to: "/kindergarten", labelKey: "nav.kg_dashboard", icon: BookOpen, perm: "kg.view", module: "kindergarten" },
            { to: "/kindergarten/incidents", labelKey: "nav.kg_incidents", icon: ScrollText, perm: "kg.view", module: "kindergarten" },
        ],
    },
    {
        to: "/finance", labelKey: "nav.finance", icon: Wallet, module: "finance",
        children: [
            // Kept in lock-step with Sidebar.tsx — auto-billing removed from
            // the tenant UI in Phase UI-2; "Fakturalar" now reads "Gələcək
            // mədaxil" via the shared translation key.
            { to: "/finance/invoices", labelKey: "nav.invoices", icon: ScrollText, perm: "finance.view", module: "finance.invoices" },
            { to: "/finance/payments", labelKey: "nav.payments", icon: Wallet, perm: "finance.view", module: "finance.payments" },
            { to: "/finance/debt", labelKey: "nav.debt", icon: ScrollText, perm: "finance.view", module: "finance.payments" },
            { to: "/finance/cash", labelKey: "nav.cash", icon: Wallet, perm: "finance.view", module: "finance.payments" },
            { to: "/finance/pricing-plans", labelKey: "nav.pricing_plans", icon: ScrollText, perm: "finance.view", module: "finance.invoices" },
        ],
    },
    {
        to: "/procurement", labelKey: "nav.procurement", icon: ShoppingCart, module: "procurement",
        children: [
            { to: "/procurement/requests", labelKey: "nav.procurement_requests", icon: FileText, perm: "procurement.view", module: "procurement" },
            { to: "/procurement/vendors", labelKey: "nav.procurement_vendors", icon: Truck, perm: "procurement.view", module: "procurement" },
        ],
    },
    {
        to: "/inventory", labelKey: "nav.inventory", icon: Boxes, module: "inventory",
        children: [
            { to: "/inventory/items", labelKey: "nav.inventory_items", icon: Package, perm: "inventory.view", module: "inventory" },
            { to: "/inventory/movements", labelKey: "nav.inventory_movements", icon: ScrollText, perm: "inventory.view", module: "inventory" },
            { to: "/inventory/equipment", labelKey: "nav.inventory_equipment", icon: Wrench, perm: "inventory.view", module: "inventory" },
        ],
    },
    { to: "/reports", labelKey: "nav.reports", icon: BarChart3, perm: "reports.view", module: "reports" },
    {
        to: "/settings", labelKey: "nav.settings", icon: Settings, module: "settings",
        children: [
            { to: "/settings/branches", labelKey: "nav.branches", icon: Building2, perm: "branch.view", module: "settings.branches" },
            { to: "/settings/rooms", labelKey: "nav.rooms", icon: Building2, perm: "room.view", module: "settings.rooms" },
            { to: "/settings/roles", labelKey: "nav.roles", icon: ShieldCheck, perm: "role.view", module: "settings.roles" },
            { to: "/settings/staff", labelKey: "nav.staff", icon: UserCog, perm: "staff.view", module: "settings.staff" },
            { to: "/settings/institution", labelKey: "nav.institution", icon: Building2, perm: "institution.view", module: "settings.institution" },
            { to: "/settings/crm", labelKey: "nav.crm_settings", icon: Users, perm: "crm.view", module: "crm" },
            { to: "/settings/audit", labelKey: "nav.audit", icon: ScrollText, perm: "audit.view", module: "settings.audit" },
            // Phase UI-2: Təhlükəsizlik/Geliştirici/Məxfilik/İnteqrasiyalar
            // removed from the tenant surface.
        ],
    },
];

export function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { t } = useTranslation();
    const { hasPerm, activeMembership, hasModuleAccess, me } = useAuth();
    const [expanded, setExpanded] = useState<Record<string, boolean>>({ "/finance": true, "/settings": true, "/kindergarten": true, "/hr": true });
    const acts = activeMembership?.tenant.activity_types || [];
    const isPO = !!me?.user?.is_platform_owner;
    // Same visibility rules as the desktop sidebar so mobile users never see
    // menu entries the platform owner has disabled.
    const canSee = (i: Item) =>
        (!i.perm || hasPerm(i.perm)) &&
        (!i.activity || acts.includes(i.activity)) &&
        (!i.module || isPO || hasModuleAccess(i.module, "view"));

    const NAV_FINAL: Item[] = isPO
        ? [{ to: "/platform", labelKey: "nav.platform", icon: Globe }, ...NAV]
        : NAV;
    // Parent is only shown when it (a) passes its own gates AND (b) has at
    // least one visible child; leaf entries just need to pass gates.
    const visibleTop = NAV_FINAL.filter((i) => (i.children ? (canSee(i) && i.children.some(canSee)) : canSee(i)));

    if (!open) return null;
    return (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer" onClick={onClose} style={{ background: "rgba(0,0,0,0.55)" }}>
            <aside
                onClick={(e) => e.stopPropagation()}
                className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] surface-menu flex flex-col shadow-2xl"
                style={{ background: "var(--menu)" }}
            >
                <div className="flex items-center justify-between px-4 shrink-0" style={{ height: 64, borderBottom: "1px solid var(--border)" }}>
                    <div className="flex items-center gap-2">
                        <Logo variant="icon" height={32} testId="mobile-brand-mark" />
                        <span className="text-sm font-semibold tracking-tight" style={{ color: "var(--text)" }} data-testid="mobile-brand-name">Service LAB</span>
                    </div>
                    <button data-testid="mobile-drawer-close" onClick={onClose} aria-label="close" className="rounded-md p-2" style={{ color: "var(--text-muted)", minHeight: 44, minWidth: 44 }}><X size={18}/></button>
                </div>
                <nav className="flex-1 overflow-y-auto overscroll-contain px-2 py-3" aria-label="mobile primary">
                    {visibleTop.map((item) => {
                        const Icon = item.icon;
                        if (item.children) {
                            const isOpen = !!expanded[item.to];
                            const visibleChildren = item.children.filter(canSee);
                            return (
                                <div key={item.to} className="mb-1">
                                    <button
                                        data-testid={`mobile-${item.labelKey.replace(/\./g, "-")}`}
                                        onClick={() => setExpanded((v) => ({ ...v, [item.to]: !v[item.to] }))}
                                        className="w-full flex items-center gap-3 px-3 rounded-md text-sm hover:bg-[var(--selected-bg)] transition-colors"
                                        style={{ color: "var(--text-muted)", minHeight: 44 }}
                                    >
                                        <Icon size={18} />
                                        <span className="flex-1 text-left">{t(item.labelKey)}</span>
                                        <ChevronDown size={14} className={isOpen ? "rotate-180 transition-transform" : "transition-transform"}/>
                                    </button>
                                    {isOpen && (
                                        <div className="ml-6 mt-1 border-l pl-2" style={{ borderColor: "var(--border)" }}>
                                            {visibleChildren.map((c) => (
                                                <NavLink key={c.to} to={c.to} end onClick={onClose}
                                                    className="w-full flex items-center gap-2 px-2 rounded-md text-xs hover:bg-[var(--selected-bg)] transition-colors"
                                                    style={({ isActive }) => ({ color: isActive ? "var(--text)" : "var(--text-muted)", background: isActive ? "var(--selected-bg)" : "transparent", minHeight: 40 })}
                                                    data-testid={`mobile-${c.labelKey.replace(/\./g, "-")}`}
                                                >
                                                    <c.icon size={16} />
                                                    <span className="truncate">{t(c.labelKey)}</span>
                                                </NavLink>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        }
                        return (
                            <NavLink key={item.to} to={item.to} end onClick={onClose}
                                className="mb-1 w-full flex items-center gap-3 px-3 rounded-md text-sm hover:bg-[var(--selected-bg)] transition-colors"
                                style={({ isActive }) => ({ color: isActive ? "var(--text)" : "var(--text-muted)", background: isActive ? "var(--selected-bg)" : "transparent", minHeight: 44 })}
                                data-testid={`mobile-${item.labelKey.replace(/\./g, "-")}`}
                            >
                                <Icon size={18} />
                                <span className="truncate">{t(item.labelKey)}</span>
                            </NavLink>
                        );
                    })}
                </nav>
            </aside>
        </div>
    );
}
